/**
 * The game flow (GDD §7.1): loading → title (the attract demo flies behind the title) → play → crash → respawn →
 * game over → title, with pause on top. The flow turns inputs into commands, simulation events into banners, the
 * crash flash and haptics, and fills the HUD's input every frame. It only reads and writes preallocated state while
 * the game runs (GDD §10.5).
 */
import type { GameAudio } from '../audio/gameAudio.ts';
import { config } from '../core/config.ts';
import type { Game } from '../game/game.ts';
import { mergeControls, type Command, type ControlSource } from '../input/actions.ts';
import { createGamepads, type Gamepads } from '../input/gamepad.ts';
import { createHaptics, type Haptics } from '../input/haptics.ts';
import { createKeyboard, type Keyboard } from '../input/keyboard.ts';
import { createTouchControls, type TouchControls } from '../input/touch.ts';
import { announce, createAnnouncer, resetAnnouncer, stepAnnouncer, type Announcer } from '../rules/announcer.ts';
import { writeRunProgress } from '../rules/checkpoint.ts';
import type { GameSim } from '../sim/context.ts';
import { NEUTRAL, type PilotInput } from '../sim/pilot.ts';
import type { SimEvent } from '../sim/state.ts';
import { saveSettings, type Settings } from '../storage/storage.ts';
import { createHud, createHudInput, type FlowState, type Hud, type HudInput } from '../hud/hud.ts';
import type { PauseAction } from '../hud/pauseMenu.ts';

/** Seconds the enemy-ability chips stay up on touch screens after a level starts or a jet respawns. */
const ABILITY_SECS = 6;

export interface FlowOptions {
  game: Game;
  settings: Settings;
  width: number;
  height: number;
  /** The game's audio (M6); absent in tests that don't need it. */
  audio?: GameAudio;
  win?: Window;
  doc?: Document;
}

export interface Flow {
  readonly state: FlowState;
  readonly paused: boolean;
  readonly hud: Hud;
  readonly settings: Settings;
  readonly announcer: Announcer;
  /** The title screen comes up (after loading). */
  ready(): void;
  command(cmd: Command): void;
  resize(w: number, h: number): void;
  /** The flow's state for tests. */
  snapshot(): FlowSnapshot;
}

export interface FlowSnapshot {
  state: FlowState;
  paused: boolean;
  menu: string | null;
  layout: string;
  /** Panel and control scales (judgment call 26). */
  scale: { panels: number; controls: number };
  touch: boolean;
  banner: { showing: boolean; key: string; kind: string; id: number };
  settings: Settings;
  pilot: PilotInput | null;
  autoFire: boolean;
  sensitivity: number;
}

/** Touch is the default where the main pointer is coarse (judgment call 32). */
function coarsePointer(win: Window): boolean {
  try {
    return win.matchMedia?.('(pointer: coarse)').matches ?? false;
  } catch {
    return false;
  }
}

export function createFlow(o: FlowOptions): Flow {
  const win = o.win ?? window;
  const doc = o.doc ?? document;
  const game = o.game;
  const audio = o.audio ?? null;
  if (!game.sim) throw new Error('game flow: the game has no simulation (classic mode only)');
  const sim: GameSim = game.sim;
  let settings: Settings = { ...o.settings };
  let state: FlowState = 'loading';
  const announcer = createAnnouncer();
  const input: HudInput = createHudInput(announcer);
  const player: PilotInput = { ...NEUTRAL };
  const haptics: Haptics = createHaptics(win.navigator);
  // abilitySecs: on touch screens the enemy-ability chips show for a few seconds after each level starts.
  const run = { runs: sim.run.runs, hiAtStart: sim.run.hi, menuFromPlay: false, abilitySecs: ABILITY_SECS };

  // Inputs (judgment call 32: keys or a gamepad leave the touch layout, a finger brings it back).
  const cmd = (c: Command): void => flow.command(c);
  const keyboard: Keyboard = createKeyboard(win, cmd, () => setTouch(false));
  const pads: Gamepads = createGamepads(win, win.navigator, cmd);
  const holder: { hud: Hud | null } = { hud: null };
  const touch: TouchControls = createTouchControls(() => holder.hud?.plan.controls.zoom ?? 1);
  const sources: ControlSource[] = [keyboard.source, pads.source, touch.source];

  const hud: Hud = createHud({
    doc,
    width: o.width,
    height: o.height,
    touch: coarsePointer(win),
    settings,
    hapticsSupported: haptics.supported,
    events: {
      pauseButton: () => flow.command('pause'),
      settingsButton: () => openSettings(),
      tapStart: () => flow.command('start'),
      pauseMenu: (a) => menuAction(a),
      settingsBack: () => flow.command('back'),
      settingsChange: (s) => {
        applySettings(s, true);
        refresh();
      },
      rebind: (els) => touch.bind(els),
    },
  });
  holder.hud = hud;

  function setTouch(on: boolean): void {
    if (hud.touch === on) return;
    hud.touch = on;
    if (!on) touch.release();
    game.autoFire = settings.autoFire && hud.touch;
  }
  // Any finger on the screen brings up the touch controls.
  win.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType === 'touch') setTouch(true);
    },
    { capture: true, passive: true },
  );

  function applySettings(s: Settings, save: boolean): void {
    settings = { ...s };
    game.sensitivity = settings.sensitivity;
    game.autoFire = settings.autoFire && hud.touch;
    haptics.enabled = settings.haptics;
    input.autoFire = settings.autoFire;
    input.leftHanded = settings.leftHanded;
    audio?.settings(settings);
    if (save) saveSettings(settings);
  }
  applySettings(settings, false);
  hud.syncSettings(settings);

  // While paused no steps run, so a menu polls the gamepads itself, once a frame.
  const menuPoll = { raf: 0 };
  const pollMenu = (): void => {
    menuPoll.raf = 0;
    if (!hud.menu || !game.paused) return;
    pads.poll();
    menuPoll.raf = win.requestAnimationFrame(pollMenu);
  };

  function setMenu(kind: 'pause' | 'settings' | null): void {
    hud.openMenu(kind);
    keyboard.menu = pads.menu = kind !== null;
    if (kind) touch.release();
    if (kind && game.paused && !menuPoll.raf) menuPoll.raf = win.requestAnimationFrame(pollMenu);
  }

  function pause(menu: 'pause' | 'settings' = 'pause'): void {
    if (!game.paused) {
      game.pause();
      audio?.pause();
      input.paused = true;
      refresh();
    }
    setMenu(menu);
  }

  function resume(): void {
    setMenu(null);
    if (game.paused) {
      input.paused = false;
      game.resume();
      audio?.resume();
    }
  }

  function openSettings(): void {
    if (state === 'title') {
      run.menuFromPlay = false;
      setMenu('settings');
    } else {
      run.menuFromPlay = true;
      pause('settings');
    }
  }

  function startRun(): void {
    run.hiAtStart = sim.run.hi;
    state = 'play';
    game.pilot = player;
    game.takeOver();
  }

  function toTitle(): void {
    state = 'title';
    game.pilot = null;
    touch.release();
  }

  function menuAction(a: PauseAction): void {
    runMenuAction(a);
    refresh();
  }

  function runMenuAction(a: PauseAction): void {
    switch (a) {
      case 'resume':
        resume();
        break;
      case 'settings':
        run.menuFromPlay = true;
        setMenu('settings');
        break;
      case 'restart':
        run.hiAtStart = sim.run.hi;
        state = 'play';
        game.pilot = player;
        game.restart();
        resume();
        break;
      case 'quit':
        toTitle();
        game.quit();
        resume();
        break;
    }
  }

  // Auto-pause when the tab or app loses focus (GDD §7.1).
  const autoPause = (): void => {
    if ((state === 'play' || state === 'over') && !game.paused) pause();
  };
  win.addEventListener('blur', autoPause);
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') autoPause();
  });

  // Simulation events → banners, the crash flash, haptics and the flow (GDD §7.1, §7.3).
  function onEvent(e: SimEvent): void {
    const r = sim.run;
    const playing = state === 'play' || state === 'over';
    audio?.event(e);
    switch (e.type) {
      case 'levelStart':
        if (r.runs !== run.runs) {
          run.runs = r.runs;
          resetAnnouncer(announcer);
          // After a player's GAME OVER the title returns with the demo (GDD §4.6, D10).
          if (state === 'over' && !r.player) toTitle();
        }
        if (state === 'play') announce(announcer, 'level', e.level);
        run.abilitySecs = ABILITY_SECS;
        break;
      case 'bridgeDestroyed':
        if (playing) announce(announcer, 'bridge');
        break;
      case 'extraJet':
        if (playing) announce(announcer, 'extraJet');
        break;
      case 'jetLost':
        if (state === 'play') {
          announce(announcer, 'jetLost', e.level, e.cause);
          hud.crash();
          haptics.pulse('crash');
        }
        break;
      case 'gameOver':
        if (state === 'play' && r.player) {
          state = 'over';
          input.final = r.score;
          input.finalHigh = r.hi;
          input.newHigh = r.score > run.hiAtStart && r.score > 0;
          announce(announcer, 'gameOver');
          touch.release();
        }
        break;
      case 'respawn':
        run.abilitySecs = ABILITY_SECS;
        break;
    }
  }

  function refresh(): void {
    const r = sim.run;
    input.flow = state;
    input.score = r.score;
    input.hi = r.hi;
    input.fuel = r.fuel;
    input.lives = r.lives;
    input.level = r.level;
    input.engaged = sim.bridge.engaged;
    writeRunProgress(sim, input);
    input.titleFade = state === 'play' ? r.title / config.flow.titleFadeSec : state === 'title' ? 1 : 0;
    // Desktop keeps the chips up; touch screens, short of room, show them briefly (Mehdi, 2026-10-10).
    input.showAbilities = hud.layout === 'landscape' || run.abilitySecs > 0;
    hud.update(input);
  }

  game.hooks.input = () => {
    pads.poll();
    mergeControls(player, sources);
  };
  game.hooks.event = onEvent;
  game.hooks.step = (dt) => {
    stepAnnouncer(announcer, dt);
    if (run.abilitySecs > 0) run.abilitySecs -= dt;
  };
  game.hooks.render = () => {
    refresh();
    audio?.frame(state);
  };
  game.hooks.boom = (size) => {
    if (state === 'play' && sim.run.player) haptics.pulse('boom', size);
  };

  function runCommand(c: Command): void {
    const menu = hud.menu;
    switch (c) {
      case 'start':
        if (state === 'title' && !menu) startRun();
        break;
      case 'confirm':
        if (menu) hud.confirm();
        else if (state === 'title') startRun();
        break;
      case 'pause':
      case 'menu':
        if (menu) {
          if (game.paused) resume();
          else setMenu(null);
        } else if (state === 'play' || state === 'over') pause();
        else if (state === 'title' && c === 'menu') startRun();
        break;
      case 'back':
        if (menu === 'settings' && run.menuFromPlay) setMenu('pause');
        else if (menu) resume();
        break;
      case 'up':
        hud.moveFocus(-1);
        break;
      case 'down':
        hud.moveFocus(1);
        break;
    }
  }

  const flow: Flow = {
    get state() {
      return state;
    },
    get paused() {
      return game.paused;
    },
    hud,
    get settings() {
      return settings;
    },
    announcer,
    ready() {
      sim.run.demo = true;
      sim.run.player = false;
      toTitle();
      refresh();
    },
    command(c) {
      runCommand(c);
      refresh();
    },
    resize(w, h) {
      hud.resize(w, h);
      refresh();
    },
    snapshot() {
      return {
        state,
        paused: game.paused,
        menu: hud.menu,
        layout: hud.layout,
        scale: { panels: hud.plan.panels.zoom, controls: hud.plan.controls.zoom },
        touch: hud.touch,
        banner: {
          showing: announcer.showing,
          key: announcer.current.key,
          kind: announcer.current.kind,
          id: announcer.id,
        },
        settings: { ...settings },
        pilot: game.pilot ? { ...game.pilot } : null,
        autoFire: game.autoFire,
        sensitivity: game.sensitivity,
      };
    },
  };
  return flow;
}
